import React, { useState, useRef } from 'react'
import { Body, Box, H2, H4 } from '../layout'
import { assetUrl } from '../../utilities'
import { sections } from '../../howItWorks'
import { Point } from '../widgets'
import { useIsMobile } from '../../hooks'
import { useGSAP } from '@gsap/react'
import ScrollTrigger from 'gsap/ScrollTrigger'
import gsap from 'gsap'

export function HowItWorks() {
  const [activeIndex, setActiveIndex] = useState(0)
  const sectionRefs = useRef([])
  const videoContainerRef = useRef()
  const pointsContainerRef = useRef()
  const videoRefs = useRef([])
  const { isMobile } = useIsMobile()

  useGSAP(() => {
    sectionRefs.current.forEach((section, index) => {
      ScrollTrigger.create({
        trigger: section,
        start: "top 60%",
        end: "bottom 60%",
        onEnter: () => setActiveIndex(index),
        onEnterBack: () => setActiveIndex(index),
      })
    })
  }, [])

  useGSAP(() => {
    videoRefs.current.forEach((video, index) => {
      if (index === activeIndex) {
        gsap.fromTo(video, { opacity: 0, scale: 1.05 }, {
          opacity: 1,
          scale: 1,
          duration: 0.5,
          ease: "power2.out"
        })
      } else {
        gsap.to(video, { opacity: 0, duration: 0.3 })
      }
    })
  }, [activeIndex])

  useGSAP(() => {
    if(!isMobile){
      ScrollTrigger.create({
        trigger: pointsContainerRef.current,
        start: 'top 50%-=175', // half the height of the video
        end: 'bottom center',
        pin: videoContainerRef.current,
        scrub: true
      })
    }
  }, [])

  return (
    <Box
      className="section-padding"
      fullWidth
      column
      gap={60}
    >
      <H2>How it works</H2>
      <div
        style={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'flex-start',
          gap: isMobile ? '48px' : '80px',
        }}
      >
        {/* Left Column - Scrollable Text */}
        <div
          className="mobile-column"
          style={{
            width: '100%',
          }}
        >
          <div
            ref={pointsContainerRef}
            style={{
              position: 'relative',
              height: '100%',
              gap: isMobile ? '32px' : 0,
              flexDirection: isMobile ? 'column' : 'row',
              display: isMobile ? 'flex' : 'block',
              width: '100%'
            }}
          >
            {sections.map((section, index) => (
              <div
                key={section.id}
                ref={(el) => {
                  sectionRefs.current[index] = el;
                }}
                data-index={index}
                style={{
                  position: 'relative',
                  minHeight: isMobile && '50vh',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  opacity: activeIndex === index ? 1 : 0.3,
                  transition: 'all 0.7s ease-in-out',
                  gap: isMobile && 32,
                  width: '100%'
                }}
              >
                <video
                  className="mobile-only"
                  muted
                  autoPlay
                  loop
                  src={assetUrl(section.video)}
                  style={{
                    boxShadow: '5px 5px 0px 0px white',
                    border: 'solid 1px white',
                    borderRadius: 24,
                    width: "100%",
                    height: 'auto',
                    objectFit: 'contain',
                  }}
                />
                <Point height={isMobile ? '25dvh' : '50dvh'}>
                  <Box column gap={16} alignItems="start" maxWidth={500}>
                    <H4 align="left" mt={6}>{section.title}</H4>
                    <Body align="left">{section.description}</Body>
                  </Box>
                </Point>
              </div>
            ))}
          </div>
        </div>
        {/* Right Column for videos in desktop */}
        <div
          className="desktop-only"
          style={{
            width: '100%',
            position: 'sticky',
            height: isMobile ? '50vh' : 0,
            display: 'flex',
          }}
          ref={videoContainerRef}
        >
          <div
            style={{
              position: 'relative',
              width: '100%',
            }}
          >
            {sections.map((section, index) => (
              <div
                key={section.id}
                ref={(el) => {
                  videoRefs.current[index] = el;
                }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  opacity: index === 0 ? 1 : 0
                }}
              >
                <Box alignItems="center" fullHeight fullWidth>
                  <video
                    muted
                    autoPlay
                    loop
                    src={assetUrl(section.video)}
                    className="imagery-card"
                    style={{ borderRadius: '24px', transform: 'translateY(50%)' }}
                  />
                </Box>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Box>
  )
}